import type { TrainingSessionListQuery } from "@/api";
import {
  isSessionStartedWithin,
  sessionStartedWithinLabel,
  sessionStartedWithinRange,
  type SessionStartedWithin,
} from "./sessionStartedWithin";
import { CANCELLED_STATUS_GROUP, SUBMIT_STATUS_CATEGORIES } from "@/modules/training/public";

/**
 * Training 세션 목록의 모든 조회 축.
 *
 * URL 이 소유하는 값이라 새로고침·딥링크·뒤로가기 뒤에도 남는다. 어느 축도 클라이언트에서 거르지
 * 않는다. 서버 페이징이라 받은 페이지를 거르면 페이지 크기와 total 이 맞지 않는다.
 *
 * **search 와 학습 시작일 두 축은 아직 어느 계층도 처리하지 않는다.** 상류(training-infra)에 축이
 * 없고 ZERG-312 가 새로 만든다. 그때까지 서버는 이 쿼리 키를 모르므로 무시하고, 목록은 좁혀지지
 * 않는다. 상류와 registry 가 배포되면 FE 변경 없이 바로 동작한다.
 *
 * `dataset` 은 표시명이 아니라 **registry Dataset uuid** 다(ZERG-315). 상류가 비교하는 값은 등록
 * 이름(`reg-ds-<uuid>`)이다. 그래서 registry 가 uuid 를 그 이름으로 바꾼다. 표시명을 보내면 같은
 * 이름의 데이터셋을 구분할 수 없고, 이름이 바뀌면 이전 조건이 알림 없이 맞지 않게 된다. 칩 문구는
 * uuid 가 아니라 이름을 표시해야 하므로 멤버·실험처럼 이름 맵을 함께 받는다.
 */
export type SessionFilterState = {
  // Team 스코프 전용. null = 전체 멤버, Me 는 항상 null 이다.
  memberUuid: string | null;
  // 스코프 전체의 실험 하나. 상류 축이 단일 값이라 다중 선택을 두지 않는다.
  experimentId: string | null;
  // 표시 카테고리 합집합. 빈 배열 = 전체 상태.
  statusGroups: readonly string[];
  // 상태 축과 같은 자리라 statusGroups 가 비었을 때만 서버로 보낸다.
  excludeCancelled: boolean;
  includeDeleted: boolean;
  branch: string;
  hash: string;
  dataset: string;
  search: string;
  // 학습이 실제로 시작된 날(YYYY-MM-DD, 양끝 포함). 표의 "생성일"(제출 시각)과 다른 축이다.
  startedAfter: string;
  startedBefore: string;
  // 같은 축의 기간 프리셋("최근 1주"). 절대 날짜와 배타적이다. 절대 날짜가 하나라도 있으면 프리셋은
  // 비어 있고, 프리셋을 고르면 절대 날짜를 비운다. 요청 시점에 절대 날짜로 바꾼다.
  startedWithin: SessionStartedWithin | "";
};

export const EMPTY_SESSION_FILTERS: SessionFilterState = {
  memberUuid: null,
  experimentId: null,
  statusGroups: [],
  excludeCancelled: false,
  includeDeleted: false,
  branch: "",
  hash: "",
  dataset: "",
  search: "",
  startedAfter: "",
  startedBefore: "",
  startedWithin: "",
};

// URL 키. 서버가 이미 이름을 가진 축은 그 이름을 그대로 쓴다. 그래서 두 이름을 따로 기억하지 않는다.
const PARAM = {
  member: "member",
  experiment: "experiment",
  status: "status",
  excludeCancelled: "exclude_cancelled",
  includeDeleted: "deleted",
  branch: "branch",
  hash: "hash",
  dataset: "dataset",
  search: "search",
  startedAfter: "started_after",
  startedBefore: "started_before",
  startedWithin: "started_within",
} as const;

const STATUS_VALUES = new Set<string>(SUBMIT_STATUS_CATEGORIES);
// 날짜는 서버 쿼리로 보내는 값이라 형식을 여기서 검사한다. URL 은 외부 입력이다.
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function readFlag(params: URLSearchParams, key: string): boolean {
  return params.get(key) === "1";
}

function readText(params: URLSearchParams, key: string): string {
  return params.get(key)?.trim() ?? "";
}

function readDate(params: URLSearchParams, key: string): string {
  const value = readText(params, key);
  return DATE_PATTERN.test(value) ? value : "";
}

/**
 * URL → 필터 상태. 어휘 밖 상태 값과 형식이 틀린 날짜는 버린다. 절대 날짜와 프리셋이 함께 오면
 * 절대 날짜가 우선한다. 절대 날짜가 더 구체적이고, 두 표현이 한 요청에 겹치면 안 된다.
 */
export function readSessionFilters(params: URLSearchParams): SessionFilterState {
  const memberUuid = readText(params, PARAM.member);
  const experimentId = readText(params, PARAM.experiment);
  const startedAfter = readDate(params, PARAM.startedAfter);
  const startedBefore = readDate(params, PARAM.startedBefore);
  const startedWithin = readText(params, PARAM.startedWithin);
  return {
    memberUuid: memberUuid === "" ? null : memberUuid,
    experimentId: experimentId === "" ? null : experimentId,
    statusGroups: params.getAll(PARAM.status).filter((value) => STATUS_VALUES.has(value)),
    excludeCancelled: readFlag(params, PARAM.excludeCancelled),
    includeDeleted: readFlag(params, PARAM.includeDeleted),
    branch: readText(params, PARAM.branch),
    hash: readText(params, PARAM.hash),
    dataset: readText(params, PARAM.dataset),
    search: readText(params, PARAM.search),
    startedAfter,
    startedBefore,
    startedWithin:
      startedAfter === "" && startedBefore === "" && isSessionStartedWithin(startedWithin)
        ? startedWithin
        : "",
  };
}

/** 필터 상태를 URL 에 다시 쓴다(축 키만 모두 교체하고, 페이지·탭 등 다른 키는 그대로 둔다). */
export function writeSessionFilters(
  params: URLSearchParams,
  state: SessionFilterState,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of Object.values(PARAM)) {
    next.delete(key);
  }
  if (state.experimentId !== null) {
    next.set(PARAM.experiment, state.experimentId);
  }
  if (state.memberUuid !== null) {
    next.set(PARAM.member, state.memberUuid);
  }
  for (const statusGroup of state.statusGroups) {
    next.append(PARAM.status, statusGroup);
  }
  if (state.excludeCancelled) {
    next.set(PARAM.excludeCancelled, "1");
  }
  if (state.includeDeleted) {
    next.set(PARAM.includeDeleted, "1");
  }
  for (const [key, value] of [
    [PARAM.branch, state.branch],
    [PARAM.hash, state.hash],
    [PARAM.dataset, state.dataset],
    [PARAM.search, state.search],
    [PARAM.startedAfter, state.startedAfter],
    [PARAM.startedBefore, state.startedBefore],
    [PARAM.startedWithin, state.startedWithin],
  ] as const) {
    if (value.trim() !== "") {
      next.set(key, value.trim());
    }
  }
  return next;
}

/**
 * 필터 상태 → 목록 API 쿼리.
 *
 * 상태 축에서 포함(statusGroups)과 제외(excludeCancelled)를 동시에 보내면 서버가 422 로 거절한다.
 * 카테고리를 하나라도 고르면 그 선택이 이미 범위를 정하므로 제외 조건은 보내지 않는다.
 *
 * 시작일 프리셋은 여기서 절대 날짜로 바꾼다. 서버는 `started_after/before` 만 안다.
 * `nowMs` 는 테스트가 고정할 수 있도록 인자로 받는다.
 */
export function sessionFiltersToQuery(
  state: SessionFilterState,
  nowMs: number = Date.now(),
): TrainingSessionListQuery {
  const query: TrainingSessionListQuery = {};
  if (state.experimentId !== null) {
    query.experimentId = state.experimentId;
  }
  if (state.statusGroups.length > 0) {
    query.statusGroups = state.statusGroups;
  } else if (state.excludeCancelled) {
    query.excludeStatusGroup = CANCELLED_STATUS_GROUP;
  }
  if (state.includeDeleted) {
    query.includeDeleted = true;
  }
  if (state.branch !== "") {
    query.branch = state.branch;
  }
  if (state.hash !== "") {
    query.hash = state.hash;
  }
  if (state.dataset !== "") {
    query.dataset = state.dataset;
  }
  if (state.search !== "") {
    query.search = state.search;
  }
  if (state.startedWithin !== "") {
    const range = sessionStartedWithinRange(state.startedWithin, nowMs);
    query.startedAfter = range.startedAfter;
    query.startedBefore = range.startedBefore;
  } else {
    if (state.startedAfter !== "") {
      query.startedAfter = state.startedAfter;
    }
    if (state.startedBefore !== "") {
      query.startedBefore = state.startedBefore;
    }
  }
  return query;
}

/** 페이지 캐시 키의 필터 부분. 같은 조건이면 같은 문자열이어야 한다. */
export function sessionFilterSignature(state: SessionFilterState): string {
  return [
    state.memberUuid ?? "",
    state.experimentId ?? "",
    [...state.statusGroups].sort().join(","),
    state.excludeCancelled ? "1" : "",
    state.includeDeleted ? "1" : "",
    state.branch,
    state.hash,
    state.dataset,
    state.search,
    state.startedAfter,
    state.startedBefore,
    state.startedWithin,
  ].join("|");
}

// 현재 적용된 조건 하나. 다중 축은 값마다 칩 하나라서 값 단위로 해제할 수 있다.
export type SessionFilterChip = {
  axis: SessionFilterAxis;
  // 다중 축에서 해제할 값. 단일 축은 undefined 다.
  value?: string;
  label: string;
};

export type SessionFilterAxis =
  | "member"
  | "experiment"
  | "status"
  | "excludeCancelled"
  | "includeDeleted"
  | "branch"
  | "hash"
  | "dataset"
  | "search"
  | "started";

// id 축은 화면에 이름으로 표시해야 한다. 목록을 가진 쪽(패널)이 맵을 전달한다. 이름을 아직 받지
// 못했으면 id 를 그대로 쓴다. 조건은 실제로 적용되어 있으므로 칩이 사라지는 것보다 낫다.
export type SessionFilterLabels = {
  memberNameById: Readonly<Record<string, string>>;
  experimentNameById: Readonly<Record<string, string>>;
  datasetNameByUuid: Readonly<Record<string, string>>;
  statusLabelByKey: Readonly<Record<string, string>>;
};

export function sessionFilterChips(
  state: SessionFilterState,
  labels: SessionFilterLabels,
): readonly SessionFilterChip[] {
  const chips: SessionFilterChip[] = [];
  if (state.memberUuid !== null) {
    chips.push({
      axis: "member",
      label: `멤버: ${labels.memberNameById[state.memberUuid] ?? state.memberUuid}`,
    });
  }
  if (state.experimentId !== null) {
    chips.push({
      axis: "experiment",
      label: `실험: ${labels.experimentNameById[state.experimentId] ?? state.experimentId}`,
    });
  }
  for (const statusGroup of state.statusGroups) {
    chips.push({
      axis: "status",
      value: statusGroup,
      label: `상태: ${labels.statusLabelByKey[statusGroup] ?? statusGroup}`,
    });
  }
  // 상태 드롭다운의 항목이라 칩도 상태 축 문구로 표시한다.
  if (state.excludeCancelled && state.statusGroups.length === 0) {
    chips.push({ axis: "excludeCancelled", label: "상태: 취소 제외" });
  }
  if (state.includeDeleted) {
    chips.push({ axis: "includeDeleted", label: "삭제 예정 포함" });
  }
  if (state.branch !== "") {
    chips.push({ axis: "branch", label: `브랜치: ${state.branch}` });
  }
  if (state.hash !== "") {
    chips.push({ axis: "hash", label: `해시: ${state.hash}` });
  }
  if (state.dataset !== "") {
    chips.push({
      axis: "dataset",
      label: `Dataset: ${labels.datasetNameByUuid[state.dataset] ?? state.dataset}`,
    });
  }
  if (state.search !== "") {
    chips.push({ axis: "search", label: `검색: ${state.search}` });
  }
  if (state.startedWithin !== "") {
    chips.push({
      axis: "started",
      label: `Training 시작일: ${sessionStartedWithinLabel(state.startedWithin)}`,
    });
  }
  // 기간은 한쪽만 정해도 조건이다. 열린 끝은 물결로 표시한다.
  if (state.startedAfter !== "" || state.startedBefore !== "") {
    chips.push({
      axis: "started",
      label: `Training 시작일: ${state.startedAfter || "처음"} ~ ${state.startedBefore || "지금"}`,
    });
  }
  return chips;
}

/** 칩 ✕. 그 축(다중 축이면 그 값 하나)만 해제한 상태를 반환한다. */
export function clearSessionFilterAxis(
  state: SessionFilterState,
  axis: SessionFilterAxis,
  value?: string,
): SessionFilterState {
  switch (axis) {
    case "member":
      return { ...state, memberUuid: null, experimentId: null };
    case "experiment":
      return { ...state, experimentId: null };
    case "status":
      return { ...state, statusGroups: state.statusGroups.filter((key) => key !== value) };
    case "excludeCancelled":
      return { ...state, excludeCancelled: false };
    case "includeDeleted":
      return { ...state, includeDeleted: false };
    case "branch":
      return { ...state, branch: "" };
    case "hash":
      return { ...state, hash: "" };
    case "dataset":
      return { ...state, dataset: "" };
    case "search":
      return { ...state, search: "" };
    case "started":
      return { ...state, startedAfter: "", startedBefore: "", startedWithin: "" };
  }
}
